import { PropsWithChildren, createContext, useMemo } from 'react';

import { demoWorkloadsGpuWasteCostImpact } from '@cast/fixtures';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { useWorkloadsGpuWasteCostImpactQuery } from 'hooks/queries/cost';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from 'types/top-items';

import { useGpuUtilizationDataOrFixture } from '../../_hooks/useGpuUtilizationDataOrFixture';
import { mapTopMostWorkloadsGpuWasteDatapoints } from '../_utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type GpuUtilizationContextState = {
  isLoading: boolean;
  refetch?: () => void;
  error?: unknown;
  topWorkloadsGpuWaste: TopMostExpensiveGroup[];
  topWorkloadsGpuWasteDatapoints: TopMostExpensiveGroupDatapoint;
};

export const GpuUtilizationContext = createContext<GpuUtilizationContextState>(
  {} as never
);

export const GpuUtilizationProvider = ({ children }: PropsWithChildren) => {
  const { cluster } = useCluster();
  const {
    hasGpuMetrics,
    timezone,
    currentDateRange,
    adjustedDateRange,
    useListingPrices,
  } = useCostReportContext();

  const {
    isLoading: isWorkloadsGpuWasteLoading,
    data: _workloadsGpuWasteData,
    error: workloadsGpuWasteError,
    refetch: refetchWorkloadsGpuWaste,
  } = useWorkloadsGpuWasteCostImpactQuery({
    clusterIds: [cluster.id],
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
    useListingPrices,
    enabled: hasGpuMetrics,
  });

  const wasteDataFixture = useMemo(
    () => () =>
      demoWorkloadsGpuWasteCostImpact(
        adjustedDateRange[0].toISOString(),
        adjustedDateRange[1].toISOString()
      ).then((data) => data.workloads),
    [adjustedDateRange]
  );

  const workloadsGpuWasteData = useGpuUtilizationDataOrFixture(
    _workloadsGpuWasteData,
    wasteDataFixture
  );

  const { topWorkloadsGpuWaste, topWorkloadsGpuWasteDatapoints } =
    useMemo(() => {
      return mapTopMostWorkloadsGpuWasteDatapoints(
        currentDateRange,
        workloadsGpuWasteData ?? [],
        timezone
      );
    }, [workloadsGpuWasteData, currentDateRange, timezone]);

  return (
    <GpuUtilizationContext.Provider
      value={{
        topWorkloadsGpuWaste,
        topWorkloadsGpuWasteDatapoints,
        isLoading: isWorkloadsGpuWasteLoading,
        error: workloadsGpuWasteError,
        refetch: refetchWorkloadsGpuWaste,
      }}
    >
      {children}
    </GpuUtilizationContext.Provider>
  );
};
